import React, { useMemo } from 'react';
import { ButtonProps, InputProps } from '@apia/theme';
import AppCheckbox from '../../fields/Checkbox';
import useField from '../hooks/useField';
import { TDefaultFieldType } from './types';
import useValidationClass from './useValidationClass';
import Wrapper from './Wrapper';
import useFieldStatesClassNames from '../hooks/useFieldStyles';
import validationsStore from '../../../../store/validation/validationStore';
import { validateByRules } from '../../../../store/validation/validationMethods';
import { lang } from '../../../../store/validation';

export type TCheckbox = Omit<
  ButtonProps,
  'onChange' | 'defaultValue' | 'defaultChecked' | 'checked'
> &
  Pick<InputProps, 'onChange'> &
  TDefaultFieldType<string> & {
    native?: boolean;
    readOnly?: boolean;
  };

const Checkbox = React.forwardRef<HTMLButtonElement, TCheckbox>(
  (
    {
      avoidSemicolon,
      className,
      label,
      native,
      onChange: outerOnChange,
      type,
      /**
       * useField
       */
      avoidRegisterIfExists,
      formName,
      initialValue,
      name,
      removeOnUnmount,
      submitValueParser,
      validationClass,
      validationFunction,
      validationRules,
      value,
      ...props
    },
    ref,
  ) => {
    const { actualValidationFunction, actualValidationRules } =
      useValidationClass(
        useMemo(
          () => ({
            formName,
            name,
            validationClass,
            validationFunction: (currentValue: string, currentFormName) => {
              if (validationFunction)
                return validationFunction(currentValue, currentFormName);

              const currentProps = validationsStore.getFieldStore(
                currentFormName,
                name,
              );

              if (
                currentProps.validationRules?.required &&
                currentValue !== 'on' &&
                (currentValue as unknown as boolean) !== true
              )
                return lang('required');

              if (currentProps.validationRules)
                return validateByRules({
                  rules: currentProps.validationRules,
                  value: currentValue,
                });

              return true;
            },
            validationRules,
          }),
          [
            formName,
            name,
            validationClass,
            validationFunction,
            validationRules,
          ],
        ),
      );
    const { onChange, updateValidations, isTouched, ...inputProps } =
      useField<string>({
        avoidRegisterIfExists,
        formName,
        name,
        initialValue: initialValue ?? (value as string),
        removeOnUnmount,
        submitValueParser,
        validationFunction: actualValidationFunction,
        validationRules: actualValidationRules,
      });
    const stateClassName = useFieldStatesClassNames(name);

    return (
      <Wrapper
        name={name}
        label={label}
        className={`checkbox ${className ?? ''}`}
        avoidSemicolon={avoidSemicolon}
      >
        <AppCheckbox
          {...props}
          {...inputProps}
          className={stateClassName}
          native={native}
          aria-label={props['aria-label'] ?? label}
          onChange={(ev) => {
            onChange(ev.target.checked ? 'on' : 'off');
            if (outerOnChange) outerOnChange(ev);
          }}
          checked={
            (inputProps?.value as unknown as boolean) === true ||
            (inputProps.value ?? 'off') === 'on'
          }
          indeterminate={inputProps.value === 'indeterminate'}
          ref={ref}
        />
      </Wrapper>
    );
  },
);

export default React.memo(Checkbox);
